import { api } from "@/modules/api";
import { AppStore } from "@/store";
import { waitForLiveQuerySync } from "@/store/sync/utils";
import { runInAction } from "mobx";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { Duration } from "luxon";

export const cleanupNote = async ({ noteId, store }: { noteId: string; store: AppStore }) => {
  const startTime = Date.now();

  try {
    const note = await store.notes.getAsync(noteId);
    if (!note) return { success: false, error: "Note not found" };
    runInAction(() => (note.isProcessingSuggestion = true));

    try {
      const response = await api.post("/v2/notes/{note_id}/cleanup", {
        params: { path: { note_id: noteId } },
      });
      if (response.response.ok) {
        await waitForLiveQuerySync({
          syncStore: store.sync,
          query: async () => {
            try {
              const spaceAccountNote = await store.spaceAccountNotes.localTable.get(
                note.spaceAccountNoteId
              );
              return !!spaceAccountNote?.model_data?.suggestion_data;
            } catch (_) {
              return false;
            }
          },
          timeout: Duration.fromObject({ seconds: 90 }),
        });

        const endTime = Date.now();
        const elapsedTime = Math.floor((endTime - startTime) / 1000);

        const spaceAccountNote = await store.spaceAccountNotes.localTable.get(
          note.spaceAccountNoteId
        );
        const suggestionData = spaceAccountNote?.model_data?.suggestion_data;

        trackEvent(TrackedEvent.NoteCleanupStart, {
          session_id: suggestionData?.metadata?.session_id,
          note_id: noteId,
          elapsed_time: elapsedTime,
          previous_title: suggestionData?.prev_primary_label,
          previous_markdown_contents: suggestionData?.prev_markdown_content,
          suggested_markdown_contents: suggestionData?.suggested_markdown_content,
          suggested_title: suggestionData?.suggested_primary_label,
        });

        return { success: true };
      } else {
        const errorMessage = response.error?.error_message || "Unknown error";
        throw new Error(errorMessage);
      }
    } finally {
      runInAction(() => (note.isProcessingSuggestion = false));
    }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
};
